import {
  useMemo,
  useState,
} from 'react';
import {
  ButtonComponent, ButtonSize,
} from '~/_shared/baseComponents/buttons';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown';
import { useTranslation } from '~/_shared/utils/hooks';
import type { BoundaryGroup } from '~/boundary/boundary.types';
import {
  buttonStyle, dropdownTriggerStyle, labelStyle, settingStyle,
} from '~/sidebar/sidebarApps/mapTools/dataLayersTool/dataLayersTool.styles';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';

export const DataLayersToolChangeBoundaryComponent = ({
  allBoundaryGroups,
  allowedBTGs,
  initialBoundaryGroup,
  onChange,
  onDone,
}: Readonly<{
  allBoundaryGroups: ReadonlyArray<BoundaryGroup>;
  allowedBTGs: ReadonlyArray<BoundaryGroupId>;
  initialBoundaryGroup: BoundaryGroupId | null;
  onChange: (boundaryGroupId: BoundaryGroupId) => void;
  onDone?: () => void;
}>) => {
  const [t] = useTranslation();

  const [selectedBoundaryGroup, setSelectedBoundaryGroup] = useState<BoundaryGroupId | null>(initialBoundaryGroup);

  const changeBTGOptions: DropdownOption<BoundaryGroupId>[] = useMemo(() => (
    allBoundaryGroups
      .filter(group => allowedBTGs.includes(group.id))
      .map(group => ({
        name: group.name,
        value: group.id,
      }))
  ), [allowedBTGs, allBoundaryGroups]);

  const handleBTGChange = () => {
    if (!selectedBoundaryGroup) {
      return;
    }
    onChange(selectedBoundaryGroup);
    onDone?.();
  };

  return (
    <>
      <div css={settingStyle}>
        <span css={labelStyle}>
          1. {t('dataLayersTool.selectBoundaryGroup')}
        </span>
        <RegularDropdownComponent<BoundaryGroupId>
          inPortal
          onChange={setSelectedBoundaryGroup}
          options={changeBTGOptions}
          triggerStyle={dropdownTriggerStyle({ isPlaceholder: false })}
          value={selectedBoundaryGroup}
        />
      </div>
      <ButtonComponent
        css={buttonStyle}
        onClick={handleBTGChange}
        size={ButtonSize.Large}
        text={t('Continue')}
        isDisabled={selectedBoundaryGroup === null}
      />
    </>
  );
};
